import clsx from 'clsx';
import { useState, useMemo, useEffect, useRef, useId } from 'react';
import { useOutsideClick } from '@/hooks/use-outside-click';
import { useLocalization } from '@/hooks/use-localization';
import { InfoMessage } from './info-message';
import { ErrorMessage } from './error-message';

export type CustomSelectProps = {
    options: { value: string | number; name: string }[];
    value: string | number;
    onChange: (value: string | number) => void;
    label?: string;
    infoText?: string;
    searchable?: boolean;
    required?: boolean;
    placeholder?: string;
    disabled?: boolean;
    dropdownClass?: clsx.ClassValue;
    showRequiredError?: boolean;
    id?: string;
    errorMessage?: string;
    darkMode?: boolean;
};

export const CustomSelect = ({
    options,
    value,
    onChange,
    label = '',
    infoText,
    required = false,
    disabled = false,
    searchable = false,
    dropdownClass = '',
    showRequiredError = false,
    id,
    errorMessage,
    darkMode = false,
}: CustomSelectProps) => {
    const { localize } = useLocalization();
    const generatedId = useId();
    const selectId = id || generatedId;
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useOutsideClick(() => setIsOpen(false));
    const focusedContainerRef = useRef<HTMLDivElement>(null);
    const listboxRef = useRef<HTMLDivElement>(null);
    const [showInfo, setShowInfo] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectValue, setSelectValue] = useState(value);
    const [focusedItemIndex, setFocusedItemIndex] = useState<number | null>(null);
    const focusedItemIndexRef = useRef(focusedItemIndex);
    const isOpenRef = useRef(isOpen);
    const selectOptionsRef = useRef<typeof options>([]);
    const sectionHadOverflowHidden = useRef<boolean | null>(null);

    const selectOptions = useMemo(() => {
        const lowerCaseSearchValue = searchValue.toLocaleLowerCase();
        let items = [...options];

        if (lowerCaseSearchValue !== '') {
            items = items
                .filter(o => o.name.toLocaleLowerCase().includes(lowerCaseSearchValue))
                .filter((item, index, self) => index === self.findIndex(t => t.value === item.value && t.name === item.name));
        }

        selectOptionsRef.current = items;
        return items;
    }, [options, searchValue]);

    const currentValue = useMemo(() => (value ? selectOptions.find(option => option.value === value)?.name : ''), [value, selectOptions]);

    useEffect(() => {
        isOpenRef.current = isOpen;
        focusedItemIndexRef.current = focusedItemIndex;

        const section = containerRef.current?.closest('div[data-component="section"]');

        // Remember whether the section originally had overflow hidden. Sections that
        // opt into overflow (allowOverflow, e.g. when containing a speech bubble with
        // overlapPrevious) render without it, and we must not add it back on close.
        if (section && sectionHadOverflowHidden.current === null) {
            sectionHadOverflowHidden.current = section.classList.contains('r-overflow-hidden');
        }

        // Let the open dropdown escape a section that clips its overflow.
        section?.classList.remove('r-overflow-hidden');

        if (!isOpen) {
            if (sectionHadOverflowHidden.current) {
                section?.classList.add('r-overflow-hidden');
            }
            setFocusedItemIndex(0);
        }
    }, [focusedItemIndex, isOpen]);

    useEffect(() => {
        containerRef.current?.addEventListener('keydown', keyDown);
        return () => {
            containerRef.current?.removeEventListener('keydown', keyDown);
        };
    }, []);

    useEffect(() => {
        if (isOpen && searchable) {
            inputRef?.current?.focus();
        }
    }, [isOpen, searchable]);

    // Keep the keyboard-focused option in range when the filtered list shrinks
    // (e.g. while typing in the searchable input). Without this, the gray
    // highlight can fall off the end of the visible list and disappear.
    useEffect(() => {
        if (!isOpen) return;
        const lastIndex = selectOptions.length - 1;
        if (lastIndex < 0) {
            setFocusedItemIndex(null);
        } else if (focusedItemIndex === null || focusedItemIndex > lastIndex) {
            setFocusedItemIndex(0);
        }
    }, [selectOptions, isOpen, focusedItemIndex]);

    // Scroll the keyboard-focused option into view so the highlight doesn't
    // fall outside the scrollable listbox on long lists.
    useEffect(() => {
        if (!isOpen || focusedItemIndex === null) return;
        const option = listboxRef.current?.children[focusedItemIndex] as HTMLElement | undefined;
        option?.scrollIntoView({ block: 'nearest' });
    }, [focusedItemIndex, isOpen]);

    const keyDown = (e: KeyboardEvent) => {
        // Ignore Space/Enter on info button
        if ((e.key === ' ' || e.key === 'Enter') && (e.target as HTMLElement).hasAttribute('data-info-button')) {
            return;
        }

        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
                setSearchValue('');
                focusedContainerRef.current?.blur();
                break;

            case 'ArrowDown': {
                e.preventDefault();

                if (!isOpenRef.current) {
                    setIsOpen(true);
                }

                const lastIndex = selectOptionsRef.current.length - 1;
                if (lastIndex < 0) return;

                if (focusedItemIndexRef.current === null) {
                    setFocusedItemIndex(0);
                    return;
                }

                // Clamp at the last option so the highlight never disappears past the end.
                setFocusedItemIndex(Math.min(focusedItemIndexRef.current + 1, lastIndex));

                break;
            }

            case 'ArrowUp': {
                e.preventDefault();

                if (!isOpenRef.current) {
                    setIsOpen(true);
                }

                const lastIndex = selectOptionsRef.current.length - 1;
                if (lastIndex < 0) return;

                if (focusedItemIndexRef.current === null) {
                    setFocusedItemIndex(lastIndex);
                    return;
                }

                // Clamp at 0 so the highlight never disappears before the start.
                setFocusedItemIndex(Math.max(focusedItemIndexRef.current - 1, 0));

                break;
            }

            case ' ':
                e.preventDefault();
                setIsOpen(prev => !prev);
                break;

            case 'Enter':
                e.preventDefault();

                if (isOpenRef.current === false) {
                    setIsOpen(true);
                    return;
                }

                if (focusedItemIndexRef.current !== null) {
                    const option = selectOptionsRef.current[focusedItemIndexRef.current];
                    if (option?.value) {
                        onSelectValue(option.value);
                    }
                }

                break;
        }
    };

    const focusNextTabbable = () => {
        const combobox = containerRef.current;
        if (!combobox) return;
        const selector =
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const next = Array.from(document.querySelectorAll<HTMLElement>(selector)).find(
            el =>
                !combobox.contains(el) &&
                el.tabIndex !== -1 &&
                el.offsetParent !== null &&
                combobox.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
        );
        if (next) {
            next.focus();
        } else {
            focusedContainerRef.current?.blur();
        }
    };

    const onSelectValue = (value: string | number) => {
        setIsOpen(false);
        setSearchValue('');
        setSelectValue(value);
        onChange(value);
        focusNextTabbable();
    };

    return (
        <div className="r-space-y-1">
            <div
                ref={containerRef}
                role="combobox"
                aria-label={label}
                aria-haspopup="listbox"
                {...(showRequiredError && { 'aria-invalid': true })}
                aria-describedby={showRequiredError && errorMessage ? `${selectId}-error` : undefined}
                data-component="custom-select"
                className={clsx('r-relative r-rounded-xl r-outline r-outline-2 r-transition-all r-duration-300', {
                    'r-z-10': isOpen,
                    '!r-outline-red-700': showRequiredError,
                    'r-outline-gray-400 focus-within:r-outline-focus-blue': !showRequiredError,
                })}
                aria-expanded={isOpen && !disabled}
                onBlur={e => {
                    // Close the dropdown when focus leaves the combobox entirely
                    // (e.g. Tab / Shift+Tab to a sibling field). `relatedTarget`
                    // is the element receiving focus; if it's still inside the
                    // combobox (search input, info button, listbox option) we
                    // keep the dropdown open.
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                        setIsOpen(false);
                        setSearchValue('');
                    }
                }}
            >
                <div
                    tabIndex={0}
                    ref={focusedContainerRef}
                    id={selectId}
                    className={clsx('custom-focus-indicator r-relative r-rounded-xl', {
                        'r-cursor-pointer': !disabled,
                    })}
                >
                    {label ? (
                        <span
                            className={clsx('r-pointer-events-none r-absolute r-left-4 r-leading-none r-text-gray-500 r-transition-all', {
                                'r-top-2 r-text-xs': !!value || (isOpen && searchable),
                                'r-text-md r-top-[1.15rem]': !value && (!isOpen || !searchable),
                            })}
                        >
                            {label} {required ? '*' : ''}
                        </span>
                    ) : null}
                    <div
                        onClick={e => {
                            e.stopPropagation();
                            setIsOpen(prev => !prev);
                        }}
                        className={clsx(
                            'r-pointer r-block r-w-full r-rounded-xl r-border r-border-transparent r-px-4 r-pb-1 r-pt-6 r-transition-all r-duration-300',
                            {
                                'r-text-transparent': !searchable && !value,
                                'r-bg-gray-100 r-text-gray-600': disabled,
                                'r-bg-red-100': showRequiredError && !disabled,
                                'r-bg-white': !disabled && !showRequiredError,
                                'r-border-focus-blue': isOpen && !showRequiredError,
                                '!r-border-red-700': isOpen && showRequiredError,
                            }
                        )}
                    >
                        {searchable && isOpen && !disabled ? (
                            <input
                                ref={inputRef}
                                value={searchValue}
                                disabled={disabled}
                                className={clsx('r-block r-outline-none', {
                                    'r-bg-red-100': showRequiredError,
                                })}
                                onChange={({ target }) => setSearchValue(target.value)}
                            />
                        ) : (
                            <span className="r-block r-min-h-6">{currentValue}</span>
                        )}
                    </div>
                    {infoText && (
                        <button
                            type="button"
                            data-info-button
                            aria-label={localize('info-button-label')}
                            aria-expanded={showInfo}
                            className="r-absolute r-right-0 r-top-0 r-grid r-w-[2.5rem] r-cursor-pointer r-place-content-center r-rounded r-border-2 r-border-transparent r-bg-transparent r-p-0 r-py-4"
                            onClick={e => {
                                e.stopPropagation();
                                setShowInfo(!showInfo);
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Escape' && showInfo) {
                                    setShowInfo(false);
                                }
                                if (e.key === ' ' || e.key === 'Enter') {
                                    e.stopPropagation();
                                }
                            }}
                        >
                            <img width={18} height={18} src="/assets/icons/field-help.svg" alt="" aria-hidden="true" />
                        </button>
                    )}
                    <img
                        width={10}
                        height={10}
                        alt="arrow"
                        src="/assets/icons/select-arrow.svg"
                        className={clsx('r-pointer-events-none r-absolute r-top-6', infoText ? 'r-right-9' : 'r-right-[10px]', {
                            'r-rotate-180': !isOpen || disabled,
                        })}
                    />
                </div>
                {isOpen && !disabled ? (
                    <div
                        ref={listboxRef}
                        role="listbox"
                        className={clsx(
                            'r-absolute r-left-0 r-right-0 r-z-20 r-mt-2 r-max-h-[26rem] r-overflow-auto r-rounded-xl r-border r-border-gray-400 r-bg-white r-shadow-lg',
                            dropdownClass
                        )}
                    >
                        {selectOptions.map(({ value, name }, index) => {
                            const isFocusedItem = focusedItemIndex === index;
                            return (
                                <button
                                    key={index}
                                    role="option"
                                    tabIndex={-1}
                                    aria-label={name}
                                    aria-selected={value === selectValue}
                                    onClick={() => onSelectValue(value as string)}
                                    className={clsx(
                                        'r-relative r-block r-w-full r-cursor-pointer r-border-b r-border-b-gray-400 r-py-4 r-pr-4 r-text-left first:r-rounded-t-xl last:r-rounded-b-xl last:r-border-b-0 hover:r-bg-gray-100',
                                        // Reserve 4px on the left so the accent bar on the focused item
                                        // doesn't shift the text when it appears.
                                        'r-border-l-4 r-border-l-transparent r-pl-[calc(1rem-4px)]',
                                        {
                                            'r-font-semibold': value === selectValue,
                                            '!r-border-l-focus-blue r-bg-gray-200': isFocusedItem,
                                        }
                                    )}
                                >
                                    {name}
                                </button>
                            );
                        })}
                    </div>
                ) : null}
            </div>
            {((showInfo && infoText) || (showRequiredError && errorMessage)) && (
                <div className="r-ml-0.5 r-mt-1.5 r-space-y-1">
                    {showInfo && infoText && <InfoMessage message={infoText} darkMode={darkMode} />}
                    {showRequiredError && errorMessage && <ErrorMessage id={`${selectId}-error`} message={errorMessage} darkMode={darkMode} />}
                </div>
            )}
        </div>
    );
};
